"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectClaimResultSchema = exports.CollectPushSchema = exports.CollectItemSchema = exports.CollectItemStatusEnum = void 0;
const zod_1 = require("zod");
// ── Collect ─────────────────────────────────────────────────────────
//
// A collect item records: *"when this user next appears, something is waiting
// for them."* (tdd-0061 Part 2)
//
// **Not a queue**, and the name is deliberate. Nothing is dequeued in order,
// there is no worker loop, and the record has no bearing on whether the work
// happens — the producing service does that regardless. Calling it a queue
// would promise ordering, concurrency control and retry semantics it neither
// provides nor needs. The parcel-depot analogy is exact: the depot does not
// deliver, it holds the item until you come.
exports.CollectItemStatusEnum = zod_1.z.enum(["pending", "claimed", "failed", "expired"]);
exports.CollectItemSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    /** Who the item is waiting for. */
    userId: zod_1.z.string(),
    /**
     * Namespaced by producer, e.g. `image-gen.store`.
     *
     * The consumer keys off this to know what to do; auth never interprets it.
     * Auth is a front desk — it holds the ticket, not the parcel.
     */
    kind: zod_1.z.string().min(1).max(100),
    /**
     * Producer-defined pointer to the work.
     *
     * **Must contain no credentials.** That is the whole point: there is nothing
     * in a collect row worth stealing, and revocation is automatic — a banned or
     * deleted user never presents a valid JWT again, so their items never drain.
     */
    payload: zod_1.z.unknown(),
    /** Which service pushed this. From the signed service token, not a header. */
    producedBy: zod_1.z.string(),
    /** Short human-readable label, so a listing renders without fanning out. */
    label: zod_1.z.string().max(200).optional(),
    createdAt: zod_1.z.string(),
    /**
     * When the item stops being claimable.
     *
     * **MUST NOT outlive the artefact it points at.** Collect guarantees the claim
     * will be *attempted*; it guarantees nothing about what the claim operates on.
     * Derive this from the producer's own retention rather than choosing it
     * independently, so the two cannot drift apart.
     */
    expiresAt: zod_1.z.string(),
    attempts: zod_1.z.number().int().min(0).default(0),
    lastError: zod_1.z.string().optional(),
    status: exports.CollectItemStatusEnum.default("pending"),
    claimedAt: zod_1.z.string().optional(),
});
/** What a producing service sends to `POST /v1/collect`. */
exports.CollectPushSchema = zod_1.z.object({
    userId: zod_1.z.string().min(1),
    kind: zod_1.z.string().min(1).max(100),
    payload: zod_1.z.unknown().optional(),
    label: zod_1.z.string().max(200).optional(),
    expiresAt: zod_1.z.string(),
    /**
     * Producer's own idempotency key, unique within (userId, kind).
     *
     * Without it, a producer retrying a failed push — or a batch that fails to
     * store twice — would stack duplicate tickets for one parcel, and the user
     * would see the same thing waiting several times.
     */
    dedupeKey: zod_1.z.string().max(200).optional(),
});
/** What the user's client reports back after attempting a claim. */
exports.CollectClaimResultSchema = zod_1.z.object({
    success: zod_1.z.boolean(),
    error: zod_1.z.string().max(500).optional(),
});
